import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export type ExportFieldOption = { name: string; label: string; order?: number }

export type ExportParams = {
  scope: "all" | "selection"
  format: "json" | "xlsx" | "txt" | "semicolon" | "csv"
} | null

export type ExportRelationMode = "labels" | "ids"

export type ComponentTableExportDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  exportLoading: boolean
  exportParams: ExportParams
  exportAllFields: boolean
  onExportAllFieldsChange: (all: boolean) => void
  exportSelectedFields: string[]
  onExportSelectedFieldsChange: (updater: (prev: string[]) => string[]) => void
  exportSemicolonField: string
  onExportSemicolonFieldChange: (name: string) => void
  exportableFieldsFromMeta: ExportFieldOption[]
  collectionHasRelationFields: boolean
  exportRelationMode: ExportRelationMode
  onExportRelationModeChange: (mode: ExportRelationMode) => void
  onCancel: () => void
  onConfirmExport: () => void
}

export function ComponentTableExportDialog({
  open,
  onOpenChange,
  exportLoading,
  exportParams,
  exportAllFields,
  onExportAllFieldsChange,
  exportSelectedFields,
  onExportSelectedFieldsChange,
  exportSemicolonField,
  onExportSemicolonFieldChange,
  exportableFieldsFromMeta,
  collectionHasRelationFields,
  exportRelationMode,
  onExportRelationModeChange,
  onCancel,
  onConfirmExport,
}: ComponentTableExportDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!exportLoading) onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Export options
            {exportLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          </DialogTitle>
          <DialogDescription>
            {exportParams?.format === "semicolon"
              ? "Semi-colon separated export allows one field only. Choose the field to export."
              : "Choose whether to include all fields or select certain fields in the export."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {exportParams?.format === "semicolon" ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Field</label>
              <Select value={exportSemicolonField} onValueChange={onExportSemicolonFieldChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select one field" />
                </SelectTrigger>
                <SelectContent>
                  {exportableFieldsFromMeta.map((f) => (
                    <SelectItem key={f.name} value={f.name}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fields to export
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportFields"
                      checked={exportAllFields}
                      onChange={() => onExportAllFieldsChange(true)}
                      className="rounded-full border-gray-300"
                    />
                    <span className="text-sm">All fields</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportFields"
                      checked={!exportAllFields}
                      onChange={() => onExportAllFieldsChange(false)}
                      className="rounded-full border-gray-300"
                    />
                    <span className="text-sm">Select certain fields</span>
                  </label>
                </div>
              </div>
              {!exportAllFields && (
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                  {exportableFieldsFromMeta.map((f) => (
                    <label key={f.name} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={exportSelectedFields.includes(f.name)}
                        onCheckedChange={(checked) => {
                          onExportSelectedFieldsChange((prev) =>
                            checked ? [...prev, f.name] : prev.filter((x) => x !== f.name)
                          )
                        }}
                      />
                      <span className="text-sm">{f.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          {collectionHasRelationFields && (
            <div className="space-y-2 rounded-md border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Relation columns</p>
              <p className="text-xs text-muted-foreground">
                Applies to relation, multi-relation, and system-relation fields in this export.
              </p>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportRelationMode"
                    className="mt-1 rounded-full border-gray-300"
                    checked={exportRelationMode === "labels"}
                    onChange={() => onExportRelationModeChange("labels")}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Readable values</span>
                    <span className="block text-muted-foreground text-xs mt-0.5">
                      Resolve labels from the related collection (good for spreadsheets and external use).
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportRelationMode"
                    className="mt-1 rounded-full border-gray-300"
                    checked={exportRelationMode === "ids"}
                    onChange={() => onExportRelationModeChange("ids")}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Stored ids</span>
                    <span className="block text-muted-foreground text-xs mt-0.5">
                      Export uids as stored (comma-separated for multi-relations), and include an item{" "}
                      <span className="font-mono">uid</span> column for round-trip updates in Labwick.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={exportLoading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmExport}
            disabled={
              exportLoading ||
              (exportParams?.format === "semicolon"
                ? !exportSemicolonField
                : !exportAllFields && exportSelectedFields.length === 0)
            }
          >
            {exportLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting…
              </>
            ) : (
              "Export"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
